import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

function addDaysIso(iso, deltaDays) {
  try {
    if (!iso || !Number.isFinite(deltaDays)) return null;
    // Use UTC to avoid timezone offsets shifting the date (e.g., CET -> previous day in ISO).
    const d = new Date(`${iso}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return null;
    d.setUTCDate(d.getUTCDate() + deltaDays);
    return d.toISOString().split('T')[0];
  } catch (e) {
    return null;
  }
}

function isoDayIndexMonFirst(iso) {
  try {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return null;
    // JS getUTCDay: 0=Sun..6=Sat => Monday-first 0=Mon..6=Sun
    return (d.getUTCDay() + 6) % 7;
  } catch (e) {
    return null;
  }
}

function getWeekMondayIso(iso) {
  const idx = isoDayIndexMonFirst(iso);
  if (idx == null) return null;
  return addDaysIso(iso, -idx);
}

function isEmptyPlannedDay(workout) {
  if (!workout) return true;
  return String(workout.type || '').toLowerCase() === 'rest';
}

export default function MoveWorkoutModal({
  isOpen,
  onClose,
  workout,
  week,
  days,
  onConfirm
}) {
  const { t } = useTranslation();
  const [targetDay, setTargetDay] = useState(null);

  useEffect(() => {
    if (isOpen) setTargetDay(null);
  }, [isOpen]);

  const dayRows = useMemo(() => {
    const weekWorkouts = Array.isArray(week?.workouts) ? week.workouts : [];

    const dayIndex = (dayName) => (Array.isArray(days) ? days.indexOf(dayName) : -1);
    const normalizedByDay = {};
    const completedByDate = {};
    for (const w of weekWorkouts) {
      if (!w) continue;
      const dIdx = w?.date ? isoDayIndexMonFirst(w.date) : null;
      const normalizedDayName =
        dIdx != null && Array.isArray(days) && days[dIdx]
          ? days[dIdx]
          : (w.day || null);
      if (w?.date && w.completed === true) {
        completedByDate[String(w.date)] = true;
      }
      if (normalizedDayName && (!normalizedByDay[normalizedDayName] || isEmptyPlannedDay(normalizedByDay[normalizedDayName]))) {
        normalizedByDay[normalizedDayName] = w;
      }
    }

    const weekMondayIso = getWeekMondayIso(workout?.date || null);

    return (Array.isArray(days) ? days : []).map((dayName) => {
      const w = normalizedByDay[dayName] || null;
      const idx = dayIndex(dayName);
      const date = weekMondayIso && idx >= 0 ? addDaysIso(weekMondayIso, idx) : (w?.date || null);
      const empty = isEmptyPlannedDay(w);
      const done = w?.completed === true || Boolean(date && completedByDate[String(date)]);
      const skipped = w?.skipped === true;
      return { dayName, date, workout: w, empty, done, skipped };
    });
  }, [week, days, workout?.date]);

  if (!isOpen || !workout || !week) return null;

  const currentDay = workout.day;
  const currentDate = workout.date;
  const selectedTarget = targetDay || null;
  const targetRow = selectedTarget ? dayRows.find((r) => r.dayName === selectedTarget) : null;
  const targetIsSame = Boolean(selectedTarget && selectedTarget === currentDay);
  const targetDisabled = Boolean(targetRow?.done);
  const targetHasNoDate = Boolean(selectedTarget && !targetRow?.date);

  const canSubmit = Boolean(selectedTarget && !targetIsSame && !targetDisabled && !targetHasNoDate);
  const actionLabel = targetRow
    ? (targetRow.empty ? t('plan.move.modal.move_here') : t('plan.move.modal.swap_here'))
    : '';

  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4 z-50">
      <div className="bg-white w-full md:max-w-xl rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] md:max-h-[90vh] flex flex-col border border-gray-100">
        <div className="p-6 border-b border-gray-100 flex justify-between items-start gap-3 bg-white/95 backdrop-blur-md sticky top-0 z-10">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-wide text-indigo-500 mb-1">
              {t('plan.move.modal.title')}
            </div>
            <div className="text-xl md:text-2xl font-black text-gray-900 truncate tracking-tight">
              {workout.title}
            </div>
            <div className="text-xs font-bold text-gray-400 font-mono mt-1 flex items-center gap-1.5">
              <span>{currentDay}</span>
              {currentDate && <><span>·</span><span>{currentDate}</span></>}
            </div>
            <div className="text-sm font-medium text-gray-500 mt-2 leading-snug">
              {t('plan.move.modal.subtitle')}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label={t('workout.close')}
            title={t('workout.close')}
          >
            ✕
          </button>
        </div>

        <div className="p-4 md:p-6 overflow-y-auto bg-gray-50/30">
          <div className="space-y-3">
            {dayRows.map((row) => {
              const isCurrent = row.dayName === currentDay;
              const isSelected = row.dayName === selectedTarget;
              const disabled = row.done;
              const done = row.done;
              const empty = row.empty;

              if (done || isCurrent) return null;

              const rightHint = disabled
                ? t('plan.move.modal.completed_day_disabled')
                : isCurrent
                  ? t('plan.move.modal.current_day')
                  : empty
                    ? t('plan.move.modal.empty_day')
                    : t('plan.move.modal.swap_day');

              return (
                <button
                  key={row.dayName}
                  type="button"
                  disabled={disabled}
                  onClick={() => setTargetDay(row.dayName)}
                  className={[
                    'w-full text-left rounded-2xl border px-4 py-4 flex items-start justify-between gap-4 transition-all duration-200 group relative overflow-hidden',
                    disabled ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white border-gray-200 hover:border-indigo-300 hover:shadow-md cursor-pointer',
                    isSelected ? 'ring-2 ring-indigo-500 border-indigo-500 bg-indigo-50/30 shadow-sm' : '',
                    isCurrent ? 'border-indigo-200' : ''
                  ].join(' ')}
                >
                  <div className="min-w-0 relative z-10">
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`text-sm font-black uppercase tracking-wide ${isSelected ? 'text-indigo-700' : 'text-gray-900'}`}>
                        {t(`days.${row.dayName.toLowerCase()}`, row.dayName)}
                      </div>
                      {row.date ? (
                        <div className="text-[10px] font-mono font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                          {row.date}
                        </div>
                      ) : null}
                    </div>

                    <div className="text-sm truncate">
                      {empty ? (
                        <span className="text-gray-400 font-medium italic">{t('plan.move.modal.no_workout')}</span>
                      ) : (
                        <span className="font-bold text-gray-800">{row.workout?.title || t('plan.move.modal.workout')}</span>
                      )}
                    </div>
                  </div>

                  <div className={`text-[10px] font-bold uppercase tracking-wide whitespace-nowrap self-center px-2 py-1 rounded-lg ${
                      isSelected ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                  }`}>
                    {rightHint}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 md:p-6 border-t border-gray-100 bg-white flex gap-3 justify-end sticky bottom-0 z-10">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-full font-bold text-gray-600 hover:bg-gray-100 transition-colors text-sm uppercase tracking-wide"
          >
            {t('workout.cancel')}
          </button>

          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              if (!canSubmit) return;
              onConfirm?.({
                target_day: selectedTarget,
                target_date: targetRow?.date || null,
                dest_workout_id: targetRow?.workout?.id || null
              });
            }}
            className={[
              'px-6 py-2.5 rounded-full font-bold text-white shadow-lg transition-all duration-200 text-sm uppercase tracking-wide',
              canSubmit 
                ? 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
            ].join(' ')}
            title={canSubmit ? actionLabel : undefined}
          >
            {t('plan.move.modal.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}


